/**
 * 데이터 포맷팅 유틸리티 함수들입니다.
 *
 * 주요 기능:
 * - px 단위 변환 (pxToNumber)
 * - 날짜/시간 포맷팅 (dateFormatter)
 * - 가격 자동 소수점 포맷팅 (priceFormatter)
 * - 지표 입력값 문자열 생성 (getIndicatorInputString)
 * - 문자열 템플릿 치환 (formatString, formatWithContext)
 * - 트리거 이름 생성 (getTriggerName)
 *
 * @remarks
 * UI에 표시되는 숫자, 날짜, 문자열 등을 사용자 친화적으로 포맷팅합니다.
 */
import type { IndicatorConfig, TaFunction, TaFunctionId, TriggerMatchInfo } from '@bit-box/common';
import { TriggerTypeBases } from '@bit-box/common';
import { DEFAULT_DATE_FORMAT, dateFormat } from '@bit-box/common/lib/date';
import { TriggerTarget } from './enums';
import { getCurrentLocaleResources } from './i18n';

/**
 * px가 붙은 값을 숫자로 반환
 * @param val - 'px'가 포함된 문자열 또는 숫자
 * @returns 숫자 값
 */
export function pxToNumber(val: string | number): number {
  if (typeof val === 'number') {
    return val;
  }
  return Number(val.replace('px', ''));
}

/**
 * timestamp를 지정한 형식으로 반환
 * @param value
 * @returns
 */
// export const timestampFormat = (value: number | {
//   valueOf(): number;
// }, fmt = DEFAULT_DATE_FORMAT) => {
//   const val = typeof value === 'object' ? value.valueOf() : value;
//   return dayjs.utc(val).local().format(fmt)
// };

// /**
//  * timestamp를 지정한 형식으로 반환하는 함수를 반환
//  * @param fmt
//  * @returns
//  */
// export const timestampFormatter = (fmt = DEFAULT_DATE_FORMAT) => (value: number | {
//   valueOf(): number;
// }) => {
//   return timestampFormat(value, fmt);
// };

/**
 * date를 지정한 형식으로 반환하는 함수를 반환
 * @param fmt
 * @returns
 */
export const dateFormatter =
  (fmt = DEFAULT_DATE_FORMAT) =>
  (
    value:
      | number
      | Date
      | {
          valueOf(): number | Date;
        },
  ) => {
    return dateFormat(value, fmt);
  };

/**
 * 가격 정수 자리수에 따라 소수점 뒤 자리수 자동 조절하여 포맷 변환
 */
export const priceFormatter = (
  value:
    | number
    | {
        valueOf(): number;
      },
) => {
  const val = typeof value === 'object' ? value.valueOf() : value;
  if (val === null || val === undefined) {
    return '';
  }
  return val.format('auto');
};

/**
 * 지표 입력값 요약 문장 반환
 */
export const getIndicatorInputString = (
  indicator: IndicatorConfig,
  taFunctions: Map<TaFunctionId, TaFunction>,
  separator = ' ',
) => {
  const taFunc = taFunctions.get(indicator.funcId);
  const { inReal: _inReal, ...restInputs } = indicator.inputs;
  return Object.entries(restInputs)
    .filter(([inpName]) => !taFunc?.optInputs?.find(opt => opt.name === inpName)?.hidden)
    .map(([inpName, inp]) => {
      const optDef = taFunc?.optInputs?.find(opt => opt.name === inpName);
      const suffix = optDef?.displaySuffix ?? '';
      return `${inp}${suffix}`;
    })
    .join(separator);
};

/**
 * 문자열에서 ${key} 에 해당하는 value를 values에서 찾아서 교체한 후 반환
 * @param str
 * @param values
 * @returns
 */
export function formatString(str: string, values: object) {
  if (!str) return str;
  if (!values) return str;

  let result = str;
  for (const [key, value] of Object.entries(values)) {
    result = result.replaceAll(`${key}`, value);
  }
  return result;
}

/**
 * 메시지 내의 {key} 플레이스홀더를 ctx 객체의 값으로 치환
 * @param message 플레이스홀더가 포함된 메시지 (예: "프록시가 부족합니다. 필요: {requiredCount}")
 * @param ctx 치환할 값을 담은 객체 (예: { requiredCount: 3 })
 * @returns 치환된 문자열
 */
export function formatWithContext(message: string, ctx?: Record<string, unknown>): string {
  if (!message || !ctx) return message;

  return message.replace(/\{(\w+)\}/g, (match, key) => {
    const value = ctx[key];
    return value !== undefined ? String(value) : match;
  });
}

export function getTriggerName(trigger: TriggerMatchInfo, { skipTarget = false }: { skipTarget?: boolean } = {}) {
  const { enums } = getCurrentLocaleResources();

  const targetName = skipTarget
    ? ''
    : trigger.target === TriggerTarget.SESSION
      ? enums.TriggerTargetCaption[TriggerTarget.SESSION]
      : enums.PositionTypeCaption[trigger.target];
  const fromTriggerTypeBase =
    'fromTriggerType' in trigger && trigger.fromTriggerType ? TriggerTypeBases[trigger.fromTriggerType] : undefined;
  return `${targetName ? `${targetName} ` : ''}${fromTriggerTypeBase ? enums.TriggerTypeBaseCaption[fromTriggerTypeBase] + ' ' : ''}${enums.TriggerTypeCaption[trigger.type]}`;
}
